// Combat conditions for the Initiative Tracker (#61), backed by the `condition`
// Family authored in db/mrcs (issue 36). Replaces the hand-typed list that used
// to live at client/src/data/conditions.js, whose own comment anticipated this:
// "The DB's rules content could later back these with descriptions."
//
// ⚠️ THIS MODULE IMPORTS NOTHING, DELIBERATELY. It is the leaf of the graph, the
// same way `config/system.js` is — axios imports the system, never the reverse.
// `conditionName` is called from `utils/formatTracker.js`, a PURE function the
// clipboard export and its tests call synchronously; when this module briefly
// imported apiClient, `formatTracker.test.js` and `CopyTrackerButtons.test.js`
// both stopped running altogether ("Cannot use import statement outside a
// module" — Jest does not transform axios's ESM build). The fetch therefore
// lives in `config/conditionsLoader.js`, which depends on this and not the
// other way round.
//
// WHY A MODULE-SCOPE STORE. Threading an async lookup through `formatTracker`
// would change that pure signature and every caller. So the resolved list lives
// at module scope and is read synchronously.
//
// ⚠️ WHY THE FALLBACK IS NOT DEAD CODE. The `condition` Family was authored for
// dnd-3.5 first; pf-1e (the default system) authored its own in issue 1311, but
// a system that declares none (an authored one, or a site that has not loaded
// it yet) must not leave its DM with no conditions at all, a regression on the
// hand-typed list this replaces. So the fallback below is the shipped
// PF-flavoured list, kept verbatim, and it is what a system without a
// `condition` Family continues to get.
//
// A happy consequence: each system now uses its own word. Pathfinder's
// condition really is "Grappled"; the 3.5 SRD's really is "Grappling". They no
// longer have to agree.

// The list shipped before this slice — PF-flavoured, hand-typed, and now the
// fallback for any system that authors no `condition` Family.
const FALLBACK_NAMES = [
  'Blinded', 'Dazzled', 'Deafened', 'Entangled', 'Exhausted', 'Fascinated',
  'Fatigued', 'Flat-Footed', 'Frightened', 'Grappled', 'Helpless', 'Invisible',
  'Nauseated', 'Panicked', 'Paralyzed', 'Petrified', 'Pinned', 'Prone',
  'Shaken', 'Sickened', 'Stable', 'Staggered', 'Stunned', 'Unconscious',
  'Confused', 'Cowering', 'Disabled', 'Dying', 'Dead',
];

export const FALLBACK_CONDITIONS = FALLBACK_NAMES.map((name) => ({
  slug: name.toLowerCase(),
  name,
  description: '',
}));

let current = FALLBACK_CONDITIONS;
let bySlug = new Map(FALLBACK_CONDITIONS.map((c) => [c.slug, c.name]));

/** The conditions a DM can apply, for the active system. Always non-empty. */
export function getConditions() {
  return current;
}

/** Display name for a stored slug. Falls back to the slug, as it always has. */
export function conditionName(slug) {
  return bySlug.get(slug) || slug;
}

/** Verbatim rules text, when the system's Family supplied it. */
export function conditionDescription(slug) {
  const hit = current.find((c) => c.slug === slug);
  return (hit && hit.description) || '';
}

/**
 * Replace the resolved list. Called by `conditionsLoader`; an empty or
 * malformed list is ignored, so the tracker is never left without conditions.
 */
export function installConditions(list) {
  if (!Array.isArray(list) || !list.length) return current;
  current = list;
  bySlug = new Map(list.map((c) => [c.slug, c.name]));
  return current;
}

/** Test seam — restores the pre-load state. */
export function resetConditions() {
  current = FALLBACK_CONDITIONS;
  bySlug = new Map(FALLBACK_CONDITIONS.map((c) => [c.slug, c.name]));
}
